import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Bot, MessageCircle, Calendar, Trophy, RefreshCcw, ArrowRight, Settings, MapPin, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const STAGE_ORDER = ['discovered', 'interested', 'trial_scheduled', 'enrolled'];
const STAGE_META = {
  discovered:       { label: 'Discovered Intent',     icon: Bot,           tone: 'bg-violet-50 border-violet-200 text-violet-900', chip: 'bg-violet-100 text-violet-800' },
  interested:       { label: 'Interested / Form Filled', icon: Sparkles,   tone: 'bg-sky-50 border-sky-200 text-sky-900',           chip: 'bg-sky-100 text-sky-800' },
  trial_scheduled:  { label: 'Trial Scheduled',       icon: Calendar,      tone: 'bg-amber-50 border-amber-200 text-amber-900',     chip: 'bg-amber-100 text-amber-800' },
  enrolled:         { label: 'Enrolled Student',      icon: Trophy,        tone: 'bg-emerald-50 border-emerald-200 text-emerald-900', chip: 'bg-emerald-100 text-emerald-800' },
};

const SOURCE_LABELS = {
  community_board: 'Community board',
  instagram_local: 'Instagram',
  search_intent: 'Search intent',
  landing_page_bot: 'AI intake bot',
};

export default function InboundCRM() {
  const [anchor, setAnchor] = useState(null);
  const [stats, setStats] = useState(null);
  const [leadsByStage, setLeadsByStage] = useState({ discovered: [], interested: [], trial_scheduled: [], enrolled: [] });
  const [loading, setLoading] = useState(true);
  const [harvesting, setHarvesting] = useState(false);
  const [anchorOpen, setAnchorOpen] = useState(false);
  const [convertLead, setConvertLead] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [a, s, l] = await Promise.all([
        api.get('/academy/sport-anchor'),
        api.get('/academy/inbound-crm/stats'),
        api.get('/academy/inbound-crm/leads'),
      ]);
      setAnchor(a.data);
      setStats(s.data);
      const grouped = { discovered: [], interested: [], trial_scheduled: [], enrolled: [] };
      (l.data?.leads || []).forEach((row) => {
        if (grouped[row.stage]) grouped[row.stage].push(row);
      });
      setLeadsByStage(grouped);
    } catch {
      toast.error('Failed to load Inbound CRM');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleHarvest = async () => {
    try {
      setHarvesting(true);
      const { data } = await api.post('/academy/inbound-crm/harvest');
      if (data.discovered === 0) {
        toast.info(`No new leads (${data.skipped_duplicates} already in pipeline)`);
      } else {
        toast.success(`${data.discovered} new ${anchor?.prominent_sport || ''} lead${data.discovered > 1 ? 's' : ''} discovered`);
      }
      fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Harvester run failed';
      toast.error(msg);
    } finally {
      setHarvesting(false);
    }
  };

  const handleInvite = async (lead) => {
    if (!lead.phone) {
      toast.error('No phone on file — capture via the chatbot first');
      return;
    }
    try {
      const { data } = await api.post(`/academy/inbound-crm/leads/${lead.id}/invite-trial`);
      if (data.dispatched_via === 'twilio') {
        toast.success(`Invite sent via Twilio (SID: ${data.twilio_sid.slice(0, 8)}…)`);
      } else if (data.wa_link) {
        window.open(data.wa_link, '_blank', 'noopener');
        toast.success(`Opened WhatsApp · lead moved to Trial Scheduled`);
      } else {
        toast.success('Invite recorded — lead moved to Trial Scheduled');
      }
      fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Invite failed';
      toast.error(msg);
    }
  };

  const handleMoveStage = async (lead, newStage) => {
    try {
      await api.put(`/academy/inbound-crm/leads/${lead.id}/stage`, { stage: newStage });
      toast.success(`Moved to ${STAGE_META[newStage]?.label}`);
      fetchAll();
    } catch {
      toast.error('Stage move failed');
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading Inbound CRM…</div>;
  }

  const hasAnchor = !!anchor?.prominent_sport;

  return (
    <div className="space-y-5 p-6" data-testid="inbound-crm-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <Bot className="h-6 w-6 text-violet-600" /> AI Inbound Enrollment Hub
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {hasAnchor ? (
              <>Sport anchor: <span className="font-semibold text-foreground">{anchor.prominent_sport}</span>
                {(anchor.service_regions || []).length > 0 && (
                  <> · <MapPin className="inline h-3 w-3" /> {anchor.service_regions.join(' · ')}</>
                )}
                {' · '}{stats?.total || 0} active leads in pipeline
              </>
            ) : (
              <span className="text-amber-700">Set your sport anchor below to start harvesting local intent.</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setAnchorOpen(true)} data-testid="inbound-anchor-btn">
            <Settings className="h-3.5 w-3.5 mr-1" /> Sport Anchor
          </Button>
          <Button size="sm" onClick={handleHarvest} disabled={harvesting || !hasAnchor}
                  className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="inbound-harvest-btn">
            <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${harvesting ? 'animate-spin' : ''}`} />
            {harvesting ? 'Harvesting…' : 'Run Harvester'}
          </Button>
        </div>
      </div>

      {/* Kanban grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {STAGE_ORDER.map((stage) => {
          const Meta = STAGE_META[stage];
          const Icon = Meta.icon;
          const rows = leadsByStage[stage] || [];
          return (
            <Card key={stage} className={`border ${Meta.tone}`} data-testid={`inbound-column-${stage}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4" /> {Meta.label}
                  </span>
                  <span className={`inline-flex items-center justify-center min-w-[28px] h-6 rounded-full text-xs font-bold ${Meta.chip}`}
                        data-testid={`inbound-count-${stage}`}>{rows.length}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-1 max-h-[640px] overflow-y-auto">
                {rows.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground italic py-3 text-center">No leads here yet</p>
                ) : rows.map((lead) => (
                  <LeadCard key={lead.id}
                            lead={lead}
                            stage={stage}
                            onInvite={() => handleInvite(lead)}
                            onConvert={() => setConvertLead(lead)}
                            onMoveStage={(target) => handleMoveStage(lead, target)} />
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <SportAnchorDialog
        open={anchorOpen}
        onOpenChange={setAnchorOpen}
        current={anchor}
        onSaved={(saved) => { setAnchor((prev) => ({ ...prev, ...saved })); fetchAll(); }}
      />

      <ConvertDialog
        lead={convertLead}
        onClose={() => setConvertLead(null)}
        onConverted={() => { setConvertLead(null); fetchAll(); }}
      />
    </div>
  );
}

function LeadCard({ lead, stage, onInvite, onConvert, onMoveStage }) {
  const initials = (lead.parent_name || lead.student_name || '??').split(' ').map((s) => s[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2.5 shadow-sm hover:shadow-md transition-shadow"
         data-testid={`inbound-card-${lead.id}`}>
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-[10px] font-bold shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold truncate">{lead.parent_name || lead.student_name || 'Unknown'}</p>
          {lead.student_name && lead.parent_name && (
            <p className="text-[10px] text-slate-500 truncate">Student: {lead.student_name}{lead.student_age ? ` · ${lead.student_age}y` : ''}</p>
          )}
          {!lead.student_name && lead.student_age && (
            <p className="text-[10px] text-slate-500">Age {lead.student_age}</p>
          )}
          <p className="text-[10px] text-slate-400 mt-0.5 truncate" title={lead.intent_signal}>
            {SOURCE_LABELS[lead.source] || lead.source} · {lead.skill_level || 'beginner'}
          </p>
          {lead.phone && (
            <p className="text-[10px] text-slate-500">📱 {lead.phone}</p>
          )}
          {lead.preferred_center && (
            <p className="text-[10px] text-slate-500 truncate">📍 {lead.preferred_center}</p>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {stage === 'discovered' && (
          <button type="button" onClick={() => onMoveStage('interested')}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-sky-100 text-sky-800 hover:bg-sky-200"
                  data-testid={`inbound-mark-interested-${lead.id}`}>
            <ArrowRight className="h-2.5 w-2.5" /> Mark Interested
          </button>
        )}
        {(stage === 'discovered' || stage === 'interested') && lead.phone && (
          <button type="button" onClick={onInvite}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid={`inbound-invite-${lead.id}`}>
            <MessageCircle className="h-2.5 w-2.5" /> Invite via WA
          </button>
        )}
        {(stage === 'interested' || stage === 'trial_scheduled') && (
          <button type="button" onClick={onConvert}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-500 text-white hover:bg-amber-600"
                  data-testid={`inbound-convert-${lead.id}`}>
            <Trophy className="h-2.5 w-2.5" /> Convert to Student
          </button>
        )}
        {stage === 'enrolled' && (
          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">
            ✓ Onboarded
          </Badge>
        )}
      </div>
    </div>
  );
}

function SportAnchorDialog({ open, onOpenChange, current, onSaved }) {
  const [prominent, setProminent] = useState('');
  const [regions, setRegions] = useState('');

  useEffect(() => {
    if (open) {
      setProminent(current?.prominent_sport || '');
      setRegions((current?.service_regions || []).join(', '));
    }
  }, [open, current]);

  const handleSave = async () => {
    if (!prominent.trim()) {
      toast.error('Prominent sport is required');
      return;
    }
    try {
      const regionsArr = regions.split(',').map((r) => r.trim()).filter(Boolean);
      const { data } = await api.put('/academy/sport-anchor', {
        prominent_sport: prominent.trim(),
        service_regions: regionsArr,
        harvester_enabled: true,
      });
      toast.success('Sport anchor saved');
      onSaved?.(data);
      onOpenChange(false);
    } catch {
      toast.error('Save failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sport-anchor-dialog">
        <DialogHeader>
          <DialogTitle>Sport Anchor & Service Regions</DialogTitle>
          <DialogDescription>
            Lock in your academy's prominent sport so the AI harvester only surfaces relevant local leads.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="prominent-sport-input">Prominent Sport</Label>
            <Input id="prominent-sport-input" value={prominent} onChange={(e) => setProminent(e.target.value)}
                   placeholder="e.g. Football, Hockey, Badminton" data-testid="prominent-sport-input" />
          </div>
          <div>
            <Label htmlFor="service-regions-input">Service Regions (comma-separated)</Label>
            <Input id="service-regions-input" value={regions} onChange={(e) => setRegions(e.target.value)}
                   placeholder="e.g. Vijayawada, Krishna District, Guntur" data-testid="service-regions-input" />
            <p className="text-[10px] text-slate-500 mt-1">First region is used as the primary scrape anchor.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} data-testid="sport-anchor-save">Save Anchor</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConvertDialog({ lead, onClose, onConverted }) {
  const [batch, setBatch] = useState('');
  const [fee, setFee] = useState('1500');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (lead) {
      const age = lead.student_age;
      setBatch(age ? (age <= 12 ? 'U-12' : age <= 15 ? 'U-15' : age <= 17 ? 'U-17' : 'Open') : '');
      setFee('1500');
    }
  }, [lead]);

  if (!lead) return null;

  const handleConvert = async () => {
    try {
      setSubmitting(true);
      const { data } = await api.post(`/academy/inbound-crm/leads/${lead.id}/convert-to-student`, {
        batch,
        monthly_fee: parseFloat(fee) || 1500,
      });
      toast.success(`${data.student_name} enrolled · Invoice ${data.fee_custom_id} for ₹${data.fee_amount.toLocaleString('en-IN')}`);
      onConverted?.(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Conversion failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle>Convert to Student</DialogTitle>
          <DialogDescription>
            Creates a permanent student profile for <strong>{lead.student_name || lead.parent_name}</strong>
            {' '}and auto-generates the first monthly fee invoice.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="batch-input">Batch / Age Group</Label>
            <Input id="batch-input" value={batch} onChange={(e) => setBatch(e.target.value)}
                   placeholder="e.g. U-15, U-21, Open" data-testid="batch-input" />
            <p className="text-[10px] text-slate-500 mt-1">Auto-suggested based on age {lead.student_age || 'unknown'}.</p>
          </div>
          <div>
            <Label htmlFor="fee-input">Monthly Fee (₹)</Label>
            <Input id="fee-input" type="number" value={fee} onChange={(e) => setFee(e.target.value)}
                   placeholder="1500" data-testid="fee-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConvert} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="convert-confirm-btn">
            {submitting ? 'Converting…' : 'Convert + Invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
